import React, { useCallback, useEffect, useState } from "react";
import { apiClient, authFetch } from "@/lib/apiClient";
import { resizeImageFile } from "@/lib/resizeImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import PushSettingsCard from "@/components/PushSettingsCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AutoMatchHistory } from "@/components/AutoMatchHistory";
import { VendorRulesManager } from "@/components/VendorRulesManager";
import { CardsManager } from "@/components/CardsManager";
import { BookingRulesManager } from "@/components/BookingRulesManager";
import { InboxRoutingPanel } from "@/components/InboxRouting";
import { SettingsSharesSection } from "@/components/SettingsSharesSection";
import { SettingsAccountsSection } from "@/components/SettingsAccountsSection";
import { SettingsAiLearningSection } from "@/components/SettingsAiLearningSection";
import AiUsageSettingsCard from "@/components/AiUsageSettingsCard";
import SplitRulesCard from "@/components/SplitRulesCard";
import FacturXSettingsCard from "@/components/FacturXSettingsCard";
import DpaCard from "@/components/DpaCard";
import CrmConnectorCard from "@/components/CrmConnectorCard";
import { SettingsVatRatesSection } from "@/components/vat/VatRatesAndInstallments";
import { ReverseChargeSuppliersCard } from "@/components/vat/ReverseChargeSuppliersCard";
import { SettingsGlobalAllowlistSection } from "@/components/SettingsGlobalAllowlistSection";
import { SettingsPostFinanceSection } from "@/components/SettingsPostFinanceSection";
import { SettingsStrongfitSection } from "@/components/SettingsStrongfitSection";
import { SettingsWhatsAppSection } from "@/components/SettingsWhatsAppSection";
import { SettingsReceiptRobotSection } from "@/components/SettingsReceiptRobotSection";
import { SettingsEmailTrimRulesSection } from "@/components/SettingsEmailTrimRulesSection";
import { CompanyUploaderPhonesSection } from "@/components/CompanyUploaderPhonesSection";
import { SettingsTwoFactorSection } from "@/components/SettingsTwoFactorSection";
import { SettingsCostCentersSection } from "@/components/SettingsCostCentersSection";
import { SettingsDeveloperSection } from "@/components/SettingsDeveloperSection";
import { HandbookCard } from "@/components/HandbookCard";
import { toast } from "sonner";
import { Play, Loader2 } from "lucide-react";
import { useCompany } from "@/context/CompanyContext";

export function Settings() {
  const [company, setCompany] = useState(null);
  const { email: currentUserEmail, isSuperAdmin } = useAuth();
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [vatClosures, setVatClosures] = useState([]);
  // Iter117: Tab-basierte Navigation — nur aktive Section sichtbar
  const [activeSection, setActiveSection] = useState(() => {
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    return hash.startsWith("#section-") ? hash.replace("#section-", "") : "firma";
  });

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      if (h.startsWith("#section-")) setActiveSection(h.replace("#section-", ""));
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Toggle display of section-* elements basierend auf activeSection
  // Iter117: CSS-basiert (kein imperatives style.display, sonst von React überschrieben)
  useEffect(() => {
    const styleId = "settings-section-toggle-style";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    // Definierte Gruppen-Container und ihre erlaubten Sections
    const groupMap = {
      "settings-main-card": ["firma","buchhaltung","freigabe","bank","bankmatch","aimatch","vendor","branding","anhang","mahnwesen","facturx","dpa","crm","smtp","ocr","konten","payroll","booking-rules","split-rules","cards","inbox-routing"],
    };
    const cardVisible = Object.entries(groupMap).map(([key, sections]) => {
      const visible = sections.includes(activeSection);
      return `[data-settings-card="${key}"] { display: ${visible ? "" : "none"}; }`;
    }).join("\n");
    // Iter117: Alias-Sections (mehrere IDs gehören zur selben Tab-Gruppe)
    const aliasMap = {
      "konten": ["section-konten", "section-konten-accounts", "section-konten-save"],
      "payroll": ["section-konten-accounts", "section-konten-save"],
      "whatsapp": ["section-whatsapp", "section-whatsapp-phones"],
      "vat": ["section-vat", "section-vat-rates"],
      "vat-rates": ["section-vat", "section-vat-rates"],
    };
    const allowedIds = aliasMap[activeSection] || [`section-${activeSection}`];
    // Hide-Rule: alle section-* die NICHT in allowedIds sind
    const hideRule = `[data-settings-active] [id^="section-"]:not(${allowedIds.map((id) => `[id="${id}"]`).join("):not(")}) { display: none !important; }`;
    // Show-Rule: alle erlaubten anzeigen
    const showRule = allowedIds.map(
      (id) => `[data-settings-active] [id="${id}"] { display: block !important; }`
    ).join("\n");
    styleEl.textContent = `
      ${hideRule}
      ${showRule}
      ${cardVisible}
    `;
    return () => { /* keep style — wird beim nächsten Update überschrieben */ };
  }, [activeSection]);
  const [form, setForm] = useState({
    name: "",
    currency: "CHF",
    street: "",
    postal_code: "",
    city: "",
    country: "",
    uid: "",
    vat_number: "",
    phone: "",
    email: "",
    default_vat_rate: "",
    default_debtor_account_id: "",
    default_revenue_account_id: "",
    default_bank_account_id: "",
    default_cash_account_id: "",
    default_credit_card_account_id: "",
    default_salary_expense_account_id: "",
    default_creditor_account_id: "",
    default_vat_input_account_id: "",
    default_vat_output_account_id: "",
    default_vat_settlement_account_id: "",
    payroll_social_employee_account_id: "",
    payroll_social_employer_account_id: "",
    payroll_pension_employee_account_id: "",
    payroll_pension_employer_account_id: "",
    payroll_tax_withholding_account_id: "",
    smtp_host: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    smtp_from_email: "",
    ocr_provider: "",
    ocr_api_url: "",
    ocr_api_key: "",
    iban: "",
    qr_iban: "",
    bank_name: "",
    logo_data_url: "",
    primary_color: "",
    accent_color: "",
    invoice_footer: "",
    dunning_enabled: true,
    dunning_add_fee_to_total: true,
    dunning_level_1_days: 10,
    dunning_level_1_fee: 0,
    dunning_level_2_days: 20,
    dunning_level_2_fee: 20,
    dunning_level_3_days: 40,
    dunning_level_3_fee: 40,
    dunning_interest_rate_percent: 0,
    auto_bank_match_enabled: false,
    auto_bank_match_notify_email: "",
    ai_match_enabled: true,
    ai_match_confidence_threshold: 0.85,
    fte_average: "",
    notes_principles: "",
    vat_method: "effective",
    vat_accounting_basis: "agreed",
    vat_saldo_rate: "",
    vat_mixed_use_key: "",
    payment_approval_enabled: false,
    payment_approval_owner_self: true,
    four_eyes_enabled: false,
    vat_registered: false,
    vat_uid: "",
    default_vat_input_account_id: "",
    autobook_enabled: false,
    vat_reporting_period: "quarterly",
    accounting_mode: "double",
    legal_form: "",
  });
  const [saving, setSaving] = useState(false);
  const [autoMatchRunning, setAutoMatchRunning] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const [smtpTestTo, setSmtpTestTo] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  const { company: ctxCompany } = useCompany();
  const companyId = ctxCompany?._id || localStorage.getItem("demoCompanyId");

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!companyId) {
          // Noch keine Firma für diesen Benutzer angelegt
          setLoadingCompany(false);
          return;
        }

        const [companyRes, accountsRes, sharesRes] = await Promise.all([
          apiClient.get(`/my/companies/${companyId}`),
          apiClient.get(`/companies/${companyId}/accounts`),
          apiClient.get(`/my/companies/${companyId}/shares`),
        ]);
        apiClient.get(`/companies/${companyId}/vat/closures`).then((r) => setVatClosures(r.data || [])).catch(() => setVatClosures([]));
        setAccounts(accountsRes.data || []);
        setForm({
          name: companyRes.data.name,
          currency: companyRes.data.currency,
          street: companyRes.data.street || "",
          postal_code: companyRes.data.postal_code || "",
          city: companyRes.data.city || "",
          country: companyRes.data.country || "",
          uid: companyRes.data.uid || "",
          vat_number: companyRes.data.vat_number || "",
          phone: companyRes.data.phone || "",
          email: companyRes.data.email || "",
          default_vat_rate:
            (companyRes.data.default_vat_rate ?? "") === ""
              ? ""
              : String(companyRes.data.default_vat_rate),
          default_debtor_account_id: companyRes.data.default_debtor_account_id || "",
          default_revenue_account_id: companyRes.data.default_revenue_account_id || "",
          default_bank_account_id: companyRes.data.default_bank_account_id || "",
          default_cash_account_id: companyRes.data.default_cash_account_id || "",
          default_credit_card_account_id: companyRes.data.default_credit_card_account_id || "",
          default_salary_expense_account_id:
            companyRes.data.default_salary_expense_account_id || "",
          default_creditor_account_id: companyRes.data.default_creditor_account_id || "",
          default_vat_input_account_id: companyRes.data.default_vat_input_account_id || "",
          default_vat_output_account_id: companyRes.data.default_vat_output_account_id || "",
          default_vat_settlement_account_id:
            companyRes.data.default_vat_settlement_account_id || "",
          payroll_social_employee_account_id: companyRes.data.payroll_social_employee_account_id || "",
          payroll_social_employer_account_id: companyRes.data.payroll_social_employer_account_id || "",
          payroll_pension_employee_account_id: companyRes.data.payroll_pension_employee_account_id || "",
          payroll_pension_employer_account_id: companyRes.data.payroll_pension_employer_account_id || "",
          payroll_tax_withholding_account_id: companyRes.data.payroll_tax_withholding_account_id || "",
          smtp_host: companyRes.data.smtp_host || "",
          smtp_port: companyRes.data.smtp_port ? String(companyRes.data.smtp_port) : "",
          smtp_username: companyRes.data.smtp_username || "",
          smtp_password: companyRes.data.smtp_password || "",
          smtp_use_tls: companyRes.data.smtp_use_tls ?? true,
          smtp_from_email: companyRes.data.smtp_from_email || "",
          ocr_provider: companyRes.data.ocr_provider || "",
          ocr_api_url: companyRes.data.ocr_api_url || "",
          ocr_api_key: companyRes.data.ocr_api_key || "",
          iban: companyRes.data.iban || "",
          qr_iban: companyRes.data.qr_iban || "",
          bank_name: companyRes.data.bank_name || "",
          logo_data_url: companyRes.data.logo_data_url || "",
          primary_color: companyRes.data.primary_color || "",
          accent_color: companyRes.data.accent_color || "",
          invoice_footer: companyRes.data.invoice_footer || "",
          dunning_enabled: companyRes.data.dunning_enabled ?? true,
          dunning_add_fee_to_total: companyRes.data.dunning_add_fee_to_total ?? true,
          dunning_level_1_days: companyRes.data.dunning_level_1_days ?? 10,
          dunning_level_1_fee: companyRes.data.dunning_level_1_fee ?? 0,
          dunning_level_2_days: companyRes.data.dunning_level_2_days ?? 20,
          dunning_level_2_fee: companyRes.data.dunning_level_2_fee ?? 20,
          dunning_level_3_days: companyRes.data.dunning_level_3_days ?? 40,
          dunning_level_3_fee: companyRes.data.dunning_level_3_fee ?? 40,
          dunning_interest_rate_percent: companyRes.data.dunning_interest_rate_percent ?? 0,
          auto_bank_match_enabled: companyRes.data.auto_bank_match_enabled ?? false,
          auto_bank_match_notify_email: companyRes.data.auto_bank_match_notify_email || "",
          ai_match_enabled: companyRes.data.ai_match_enabled ?? true,
          ai_match_confidence_threshold: companyRes.data.ai_match_confidence_threshold ?? 0.85,
          fte_average:
            companyRes.data.fte_average !== null && companyRes.data.fte_average !== undefined
              ? String(companyRes.data.fte_average)
              : "",
          notes_principles: Array.isArray(companyRes.data.notes_principles)
            ? companyRes.data.notes_principles.join("\n")
            : "",
          vat_method: companyRes.data.vat_method || "effective",
          vat_accounting_basis: companyRes.data.vat_accounting_basis || "agreed",
          vat_registered: !!companyRes.data.vat_registered,
          vat_uid: companyRes.data.vat_uid || "",
          default_vat_input_account_id: companyRes.data.default_vat_input_account_id || "",
          autobook_enabled: !!companyRes.data.autobook_enabled,
          vat_mixed_use_key: companyRes.data.vat_mixed_use_key != null ? String(companyRes.data.vat_mixed_use_key) : "",
          payment_approval_enabled: !!companyRes.data.payment_approval_enabled,
          payment_approval_owner_self: companyRes.data.payment_approval_owner_self !== false,
          four_eyes_enabled: !!companyRes.data.four_eyes_enabled,
          vat_saldo_rate:
            companyRes.data.vat_saldo_rate !== null && companyRes.data.vat_saldo_rate !== undefined
              ? String(companyRes.data.vat_saldo_rate)
              : "",
          vat_reporting_period: companyRes.data.vat_reporting_period || "quarterly",
          accounting_mode: companyRes.data.accounting_mode || "double",
          legal_form: companyRes.data.legal_form || "",
        });

        // Rolle des aktuellen Benutzers aus der Company ableiten (owner oder Share)
        let role = "owner";
        if (currentUserEmail && Array.isArray(sharesRes.data?.shares)) {
          const match = sharesRes.data.shares.find((s) => s.email === currentUserEmail);
          if (match) {
            role = match.role || "advisor";
          }
        }
        setCompany({ ...companyRes.data, current_user_role: role });
      } catch (e) {
        console.error(e);
        const st = e?.response?.status;
        if (st === 401 || st === 403 || st === 404) {
          // Gespeicherte Firmen-ID ist veraltet (z.B. anderes System) → auf erste eigene Firma wechseln statt «keine Firma» anzuzeigen
          try {
            const list = (await apiClient.get(`/my/companies`)).data || [];
            if (list.length && list[0]._id !== companyId) {
              localStorage.setItem("demoCompanyId", list[0]._id);
              window.location.reload();
              return;
            }
          } catch { /* Liste nicht ladbar */ }
        }
        setLoadError(st ? `Firma konnte nicht geladen werden (HTTP ${st}).` : "Firma konnte nicht geladen werden (Verbindung).");
      }
    };

    fetchData();
  }, [companyId]);

  const isViewer = company?.current_user_role === "viewer";

  const handleAccountsChange = useCallback((next) => {
    setAccounts(next);
  }, []);

  const handleSmtpTest = async () => {
    if (!companyId) return;
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      // Erst die aktuellen SMTP-Form-Werte speichern, damit der Backend-Test sie nutzt
      const smtpPayload = {
        smtp_host: form.smtp_host || null,
        smtp_port: form.smtp_port ? Number(form.smtp_port) : null,
        smtp_username: form.smtp_username || null,
        smtp_password: form.smtp_password || null,
        smtp_use_tls: !!form.smtp_use_tls,
        smtp_from_email: form.smtp_from_email || null,
      };
      await apiClient.patch(`/my/companies/${companyId}`, smtpPayload);

      const { data } = await apiClient.post(
        `/companies/${companyId}/settings/smtp-test`,
        { to_email: smtpTestTo },
      );
      setSmtpTestResult({ ok: true, message: data?.detail || "E-Mail gesendet." });
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        "SMTP-Test fehlgeschlagen.";
      setSmtpTestResult({ ok: false, message: String(detail) });
    } finally {
      setSmtpTesting(false);
    }
  };

  const SECTION_FIELDS = {
    buchhaltung: ["accounting_mode", "legal_form"],
    firma: ["name", "currency", "uid", "vat_number", "default_vat_rate", "street", "postal_code", "city", "country", "phone", "email", "website"],
    bank: ["iban", "qr_iban", "bank_name"],
    bankmatch: ["auto_bank_match_enabled", "auto_bank_match_notify_email"],
    aimatch: ["ai_match_enabled", "ai_match_confidence_threshold"],
    branding: ["logo_data_url", "primary_color", "accent_color", "invoice_footer"],
    anhang: ["fte_average", "notes_principles"],
    mahnwesen: ["dunning_enabled", "dunning_add_fee_to_total", "dunning_interest_rate_percent", "payment_terms_days", "dunning_fee_account_id", "dunning_interest_account_id",
      ...[1, 2, 3].flatMap((l) => [`dunning_level_${l}_days`, `dunning_level_${l}_fee`, `dunning_level_${l}_subject`, `dunning_level_${l}_body`])],
    smtp: ["smtp_host", "smtp_port", "smtp_username", "smtp_password", "smtp_from_email", "smtp_use_tls"],
    ocr: ["ocr_provider", "ocr_api_url", "ocr_api_key"],
    konten: ["default_debtor_account_id", "default_revenue_account_id", "default_creditor_account_id", "default_bank_account_id", "default_cash_account_id", "default_credit_card_account_id",
      "default_salary_expense_account_id", "payroll_social_employee_account_id", "payroll_social_employer_account_id", "payroll_pension_employee_account_id", "payroll_pension_employer_account_id",
      "payroll_tax_withholding_account_id", "default_vat_settlement_account_id"],
    vat: ["vat_method", "vat_accounting_basis", "vat_registered", "vat_uid", "default_vat_input_account_id", "autobook_enabled", "vat_saldo_rate", "vat_reporting_period", "vat_mixed_use_key"],
    freigabe: ["payment_approval_enabled", "payment_approval_owner_self", "four_eyes_enabled"],
  };
  const SECTION_LABELS = { firma: "Firma", buchhaltung: "Buchhaltungsart", freigabe: "Zahlungsfreigabe", bank: "Bank / QR", bankmatch: "Auto Bank-Match", aimatch: "KI-Match", branding: "Branding", anhang: "Anhang", mahnwesen: "Mahnwesen", smtp: "E-Mail / SMTP", ocr: "OCR", konten: "Konten", vat: "MWST" };
  const [savingSection, setSavingSection] = useState(null);

  const handleSaveCompany = async (e, section = null) => {
    e?.preventDefault?.();
    setSaving(true);
    setSavingSection(section);
    try {
      // Coerce empty strings to null so Pydantic Optional[EmailStr]/Optional[int]/etc. validators
      // (which reject "") don't reject the whole payload. Booleans + numeric form-fields are
      // handled explicitly below.
      const cleaned = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      const payload = {
        ...cleaned,
        default_vat_rate:
          form.default_vat_rate === "" || form.default_vat_rate === null
            ? null
            : Number(form.default_vat_rate),
        fte_average:
          form.fte_average === "" || form.fte_average === null || form.fte_average === undefined
            ? null
            : Number(form.fte_average),
        notes_principles: form.notes_principles
          ? form.notes_principles
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
        vat_method: form.vat_method || "effective",
        vat_accounting_basis: form.vat_accounting_basis || "agreed",
        vat_registered: !!form.vat_registered,
        vat_uid: form.vat_uid || null,
        default_vat_input_account_id: form.default_vat_input_account_id || null,
        autobook_enabled: !!form.autobook_enabled,
        vat_saldo_rate:
          form.vat_saldo_rate === "" || form.vat_saldo_rate === null || form.vat_saldo_rate === undefined
            ? null
            : Number(form.vat_saldo_rate),
        vat_reporting_period: form.vat_reporting_period || "quarterly",
        vat_mixed_use_key: form.vat_mixed_use_key === "" || form.vat_mixed_use_key == null ? null : Number(form.vat_mixed_use_key),
        payment_approval_enabled: !!form.payment_approval_enabled,
        payment_approval_owner_self: form.payment_approval_owner_self !== false,
        four_eyes_enabled: !!form.four_eyes_enabled,
        ai_match_enabled: !!form.ai_match_enabled,
        ai_match_confidence_threshold:
          form.ai_match_confidence_threshold === "" || form.ai_match_confidence_threshold == null
            ? 0.85
            : Number(form.ai_match_confidence_threshold),
      };

      if (!company) {
        if (loadError) { toast.error("Firma konnte nicht geladen werden – bitte Seite neu laden statt eine neue Firma anzulegen."); return; }
        // Neue Firma anlegen
        const res = await apiClient.post(`/my/companies`, payload);
        setCompany(res.data);
        if (res.data._id) {
          localStorage.setItem("demoCompanyId", res.data._id);
        }
      } else {
        // Bestehende Firma aktualisieren – pro Bereich nur dessen Felder senden
        const keys = section && company ? SECTION_FIELDS[section] : null;
        const body = keys ? Object.fromEntries(Object.entries(payload).filter(([k]) => keys.includes(k))) : payload;
        const res = await apiClient.put(`/my/companies/${company._id}`, body);
        setCompany(res.data);
        setForm((prev) => ({
          ...prev,
          smtp_password: body.smtp_password || prev.smtp_password,
        }));
      }
      toast.success(section ? `${SECTION_LABELS[section] || "Einstellungen"} gespeichert.` : "Firma gespeichert.");
    } catch (e) {
      console.error(e);
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map((x) => `${(x.loc || []).slice(-1)[0] || "Feld"}: ${x.msg}`).join(" · ")
        : d || "Speichern fehlgeschlagen";
      toast.error(msg);
    } finally {
      setSaving(false);
      setSavingSection(null);
      setLoadingCompany(false);
    }
  };

  const SectionSave = ({ section, label }) => (
    <div className="flex justify-end pt-3" data-testid={`settings-section-save-${section}`}>
      <Button
        type="button"
        size="sm"
        className="rounded-full bg-slate-900 text-slate-50 px-4"
        disabled={saving || isViewer}
        onClick={(e) => handleSaveCompany(e, section)}
        data-testid={`settings-save-${section}-button`}
      >
        {savingSection === section ? "Speichern ..." : label || `${SECTION_LABELS[section] || "Bereich"} speichern`}
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
      {loadingCompany && !company && (
        <div className="text-sm text-slate-500">Firma wird geladen ...</div>
      )}
      {!loadingCompany && !company && loadError && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex items-center justify-between gap-3" data-testid="settings-load-error">
          <span>{loadError} Ihre Firma existiert – bitte neu laden. Speichern ist deaktiviert, damit keine zweite Firma angelegt wird.</span>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()} data-testid="settings-reload-button">Neu laden</Button>
        </div>
      )}
      {!loadingCompany && !company && !loadError && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Sie haben noch keine Firma angelegt. Bitte erfassen Sie hier Ihre Stammdaten und speichern Sie die Firma.
        </div>
      )}

        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Einstellungen
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Basisdaten Ihrer Firma, Standardwährung, Kontenrahmen, Standardkonten und Zugriffsrechte.
        </p>
      </div>

      {/* Iter98: Browser-Push Settings */}
      <PushSettingsCard />

      {/* Sidebar Sub-Nav (vertikal, gruppiert, sticky) + Content */}
      <div className="grid grid-cols-1 md:grid-cols-[240px,1fr] gap-6">
        <aside
          className="md:sticky md:top-4 self-start md:max-h-[calc(100vh-2rem)] md:overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm p-2"
          data-testid="settings-quick-nav"
        >
          {[
            {
              id: "general",
              label: "Allgemein",
              items: [
                { href: "#section-firma", label: "Firma" },
                { href: "#section-branding", label: "Branding" },
                { href: "#section-kontenrahmen", label: "Kontenrahmen" },
                { href: "#section-handbuch", label: "Handbuch (PDF)" },
              ],
            },
            {
              id: "accounting",
              label: "Buchhaltung",
              items: [
                { href: "#section-buchhaltung", label: "Buchhaltungsart" },
                { href: "#section-freigabe", label: "Zahlungsfreigabe" },
                { href: "#section-konten", label: "Standardkonten" },
                { href: "#section-bank", label: "Bank / QR" },
                { href: "#section-payroll", label: "Lohn-Konten" },
                { href: "#section-vat", label: "MWST" },
                { href: "#section-vat-rates", label: "MWST-Sätze (Gültigkeit)" },
                { href: "#section-mahnwesen", label: "Mahnwesen" },
                { href: "#section-facturx", label: "E-Rechnung Factur-X / ZUGFeRD (EU)" },
                { href: "#section-dpa", label: "Datenschutz / AVV" },
                { href: "#section-crm", label: "CRM-Anbindung" },
                { href: "#section-bankmatch", label: "Auto Bank-Match" },
                { href: "#section-aimatch", label: "KI-Match" },
                { href: "#section-vendor", label: "Auto-Lern-Konto" },
                { href: "#section-booking-rules", label: "Kontierungsregeln & Spesen" },
                { href: "#section-split-rules", label: "Split-Regeln" },
                { href: "#section-cards", label: "Firmenkarten" },
                { href: "#section-inbox-routing", label: "Auto-Buchung (Routing)" },
                { href: "#section-ai-learning", label: "KI-Lernverlauf" },
                { href: "#section-ai-usage", label: "KI-Nutzung & Credits" },
                { href: "#section-cost-centers", label: "Kostenstellen" },
              ],
            },
            {
              id: "integrations",
              label: "Integrationen",
              items: [
                { href: "#section-smtp", label: "E-Mail / SMTP" },
                { href: "#section-ocr", label: "OCR" },
                { href: "#section-postfinance", label: "PostFinance" },
                { href: "#section-app-anbindung", label: "App-Anbindung" },
                { href: "#section-whatsapp", label: "WhatsApp-Bot" },
                { href: "#section-receipt-robot", label: "Beleg-Abruf (Portale)" },
                { href: "#section-email-trim-rules", label: "E-Mail-Zuschnitte" },
                { href: "/integrations/shopify", label: "Shopify-Connector", external: true },
                { href: "/integrations/woocommerce", label: "WooCommerce-Connector", external: true },
                { href: "/integrations/ebill", label: "eBill (SIX)", external: true },
                { href: "/integrations/ebics", label: "EBICS Bank-Connector", external: true },
                { href: "/integrations/mftpf", label: "PostFinance MFTPF (SFTP)", external: true },
                { href: "#section-developer", label: "Entwickler" },
              ],
            },
            {
              id: "security",
              label: "Sicherheit & Zugriff",
              items: [
                { href: "#section-shares", label: "Zugriff" },
                { href: "#section-2fa", label: "2FA" },
                ...(isSuperAdmin
                  ? [{ href: "#section-global-allowlist", label: "Global-Allowlist" }]
                  : []),
              ],
            },
          ].map((group) => (
            <div key={group.id} className="mb-1.5" data-testid={`settings-nav-group-${group.id}`}>
              <div className="px-3 py-1.5 text-[11px] font-semibold tracking-wider uppercase text-slate-400">
                {group.label}
              </div>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const sectionKey = item.href.startsWith("#section-")
                    ? item.href.replace("#section-", "")
                    : null;
                  const isActive = sectionKey && sectionKey === activeSection;
                  return (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      onClick={(e) => {
                        if (sectionKey) {
                          e.preventDefault();
                          setActiveSection(sectionKey);
                          window.history.replaceState(null, "", item.href);
                        }
                      }}
                      className={`block rounded-md px-3 py-1.5 text-sm transition-colors ${
                        item.external
                          ? "text-emerald-700 hover:bg-emerald-50 font-medium"
                          : isActive
                          ? "bg-slate-900 text-white font-medium"
                          : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                      }`}
                      data-testid={`settings-jump-${item.href.replace("#section-", "").replace(/[/]/g, "-")}`}
                    >
                      {item.label}
                      {item.external && <span className="ml-1 text-[10px] opacity-60">↗</span>}
                    </a>
                  </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>

        <div className="min-w-0 space-y-6" data-testid="settings-content" data-settings-active={activeSection}>

      <Card
        className="p-4 border border-slate-200 bg-white shadow-sm scroll-mt-6"
        data-testid="settings-company-card"
        data-settings-card="settings-main-card"
      >
        <form className="space-y-4" onSubmit={handleSaveCompany} noValidate>
          {/* Firmendaten — Iter117: in eigenen Wrapper für Tab-Switching */}
          <div id="section-firma" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="company-name">Firmenname</Label>
              <Input
                id="company-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="settings-company-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="currency">Standardwährung</Label>
              <Select
                value={form.currency}
                onValueChange={(value) => setForm({ ...form, currency: value })}
              >
                <SelectTrigger id="currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CHF">CHF</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Adresse & Kontakt */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="uid">UID / Handelsregister-Nr.</Label>
              <Input
                id="uid"
                value={form.uid}
                onChange={(e) => setForm({ ...form, uid: e.target.value })}
                placeholder="z.B. CHE-123.456.789"
                data-testid="settings-uid-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="vat-number">MWST-Nummer</Label>
              <Input
                id="vat-number"
                value={form.vat_number}
                onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                placeholder="z.B. CHE-123.456.789 MWST"
                data-testid="settings-vat-number-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="default-vat-rate">Standard-MWST-Satz (%)</Label>
              <Input
                id="default-vat-rate"
                type="number"
                step="0.1"
                value={form.default_vat_rate}
                onChange={(e) => setForm({ ...form, default_vat_rate: e.target.value })}
                placeholder="z.B. 7.7"
                data-testid="settings-default-vat-rate-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="street">Strasse</Label>
              <Input
                id="street"
                value={form.street}
                onChange={(e) => setForm({ ...form, street: e.target.value })}
                data-testid="settings-street-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="postal-code">PLZ / Ort</Label>
              <div className="flex gap-2">
                <Input
                  id="postal-code"
                  className="w-28"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                  data-testid="settings-postal-code-input"
                />
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="settings-city-input"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="country">Land</Label>
              <Input
                id="country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="z.B. CH"
                data-testid="settings-country-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="phone">Telefon</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="settings-phone-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="settings-email-input"
              />
            </div>
          </div>

            <SectionSave section="firma" />
          </div>
          {/* /Firmendaten-Wrapper Iter117 */}

          {/* Buchhaltungsart: Milchbüchlein / doppelt */}
          <div id="section-buchhaltung" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-buchhaltung-section">
            <h2 className="text-sm font-medium text-slate-800 mb-1">Buchhaltungsart</h2>
            <p className="text-xs text-slate-500 mb-3">
              Einzelfirmen und Personengesellschaften unter CHF 500'000 Umsatz dürfen einfache Buchhaltung führen (OR 957 Abs. 2).
              Der Modus ändert nur die Oberfläche – im Hintergrund bleibt alles doppelt verbucht, ein Wechsel ist jederzeit ohne Datenverlust möglich.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="accounting-mode">Modus</Label>
                <select id="accounting-mode" value={form.accounting_mode || "double"}
                  onChange={(e) => setForm((f) => ({ ...f, accounting_mode: e.target.value }))}
                  className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="settings-accounting-mode-select">
                  <option value="double">Doppelte Buchhaltung (Bilanz &amp; Erfolgsrechnung)</option>
                  <option value="simple">Einfache Buchhaltung («Milchbüchlein»)</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="legal-form">Rechtsform</Label>
                <select id="legal-form" value={form.legal_form || ""}
                  onChange={(e) => setForm((f) => ({ ...f, legal_form: e.target.value }))}
                  className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="settings-legal-form-select">
                  <option value="">– nicht angegeben –</option>
                  {["Einzelfirma", "KlG", "KmG", "GmbH", "AG", "Verein", "Stiftung"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            {form.accounting_mode === "simple" && ["GmbH", "AG"].includes(form.legal_form) && (
              <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="settings-accounting-mode-warning">
                Achtung: Für {form.legal_form} ist die doppelte Buchhaltung gesetzlich Pflicht (OR 957 Abs. 1). Das Milchbüchlein ist hier nur als vereinfachte Ansicht sinnvoll.
              </div>
            )}
            <SectionSave section="buchhaltung" />
          </div>

          {/* Zahlungsfreigabe Vier-Augen */}
          <div id="section-freigabe" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-freigabe-section">
            <h2 className="text-sm font-medium text-slate-800 mb-1">Zahlungsfreigabe (Vier-Augen-Prinzip)</h2>
            <p className="text-xs text-slate-500 mb-3">Aktiv: Lieferantenrechnungen müssen freigegeben sein, bevor sie in einen Zahlungslauf (pain.001) dürfen. Freigeber sind die Inhaber:in und Mitglieder mit Rolle «Freigeber» (unter «Freigaben &amp; Zugriff»). Wer eine Rechnung erfasst hat, darf sie nicht selbst freigeben.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input type="checkbox" checked={!!form.payment_approval_enabled} onChange={(e) => setForm((f) => ({ ...f, payment_approval_enabled: e.target.checked }))} data-testid="settings-payment-approval-enabled" />
                Zahlungsfreigabe aktivieren
              </label>
              <label className={`flex items-center gap-2 text-sm ${(form.payment_approval_enabled || form.four_eyes_enabled) ? "text-slate-800" : "text-slate-400"}`}>
                <input type="checkbox" disabled={!form.payment_approval_enabled && !form.four_eyes_enabled} checked={form.payment_approval_owner_self !== false} onChange={(e) => setForm((f) => ({ ...f, payment_approval_owner_self: e.target.checked }))} data-testid="settings-payment-approval-owner-self" />
                Inhaber:in darf selbst erfasste Zahlungen freigeben (sinnvoll bei Einzelunternehmen)
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input type="checkbox" className="mt-0.5" checked={!!form.four_eyes_enabled} onChange={(e) => setForm((f) => ({ ...f, four_eyes_enabled: e.target.checked }))} data-testid="settings-four-eyes-enabled" />
                <span>Vier-Augen-Prinzip auch für <b>Spesen freigeben</b>, <b>Lohnlauf buchen</b> und <b>Jahresabschluss freigeben</b>
                  <span className="block text-xs text-slate-500">Wer den Vorgang erfasst hat, darf ihn nicht selbst freigeben – die Ausnahme für die Inhaber:in oben gilt auch hier.</span></span>
              </label>
            </div>
            <SectionSave section="freigabe" />
          </div>

          {/* Bankverbindung / QR-Rechnung */}
          <div id="section-bank" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2">Bankverbindung (für QR-Rechnungen)</h2>
            <p className="text-xs text-slate-500 mb-3">
              Hinterlegen Sie Ihre IBAN, damit Rechnungen mit einem Swiss QR-Zahlteil gemäss
              ISO 20022 erstellt werden können. Eine QR-IBAN ist optional und wird genutzt, sobald
              Sie QR-Referenzen verwenden.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  placeholder="z.B. CH93 0076 2011 6238 5295 7"
                  value={form.iban}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  data-testid="settings-iban-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="qr-iban">QR-IBAN (optional)</Label>
                <Input
                  id="qr-iban"
                  placeholder="z.B. CH44 3199 9123 0008 8901 2"
                  value={form.qr_iban}
                  onChange={(e) => setForm({ ...form, qr_iban: e.target.value })}
                  data-testid="settings-qr-iban-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="bank-name">Bankname (optional)</Label>
                <Input
                  id="bank-name"
                  placeholder="z.B. Raiffeisen Zürich"
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  data-testid="settings-bank-name-input"
                />
              </div>
            </div>
            <SectionSave section="bank" />
          </div>

          {/* Auto Bank-Match */}
          <div id="section-bankmatch" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2">
              Auto Bank-Match
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              Wenn aktiv, werden Bank-Transaktionen mit sehr hohem Match-Score (≥ 0.95 — praktisch
              exakter Betrag und Datum) jede Nacht um 02:45 Uhr automatisch einer Rechnung oder
              Eingangsrechnung zugeordnet. Ein Morgen-Report wird per E-Mail versendet. Bei
              Unsicherheiten einfach ausschalten — die Vorschläge erscheinen dann nur in der
              Aufgaben-Inbox und werden manuell bestätigt.
            </p>
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3">
              <input
                type="checkbox"
                id="auto-bank-match-enabled"
                checked={!!form.auto_bank_match_enabled}
                onChange={(e) =>
                  setForm({ ...form, auto_bank_match_enabled: e.target.checked })
                }
                className="h-4 w-4 rounded border-slate-300"
                data-testid="settings-auto-bank-match-toggle"
              />
              <Label
                htmlFor="auto-bank-match-enabled"
                className="cursor-pointer text-sm font-medium text-slate-900"
              >
                Auto Bank-Match aktivieren
              </Label>
              <span
                className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  form.auto_bank_match_enabled
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-slate-200 text-slate-600"
                }`}
              >
                {form.auto_bank_match_enabled ? "AN" : "AUS"}
              </span>
            </div>
            {form.auto_bank_match_enabled && (
              <div className="space-y-2">
                <Label htmlFor="auto-bank-match-email">
                  Empfänger des Morgen-Reports (optional)
                </Label>
                <Input
                  id="auto-bank-match-email"
                  type="email"
                  value={form.auto_bank_match_notify_email}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      auto_bank_match_notify_email: e.target.value,
                    })
                  }
                  placeholder="Leer = Owner-E-Mail"
                  data-testid="settings-auto-bank-match-email"
                />
                <p className="text-[11px] text-slate-500">
                  Der Report enthält die Anzahl angewendeter/übersprungener Matches und eine
                  Fehlerliste. Wird nur gesendet, wenn mindestens ein Match angewendet oder ein
                  Fehler aufgetreten ist und SMTP konfiguriert ist.
                </p>
              </div>
            )}

            {/* History-Panel + Manual Trigger */}
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Auto-Match-Historie
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    setAutoMatchRunning(true);
                    try {
                      const res = await apiClient.post("/scheduler/bank-match/run-now");
                      const d = res.data || {};
                      toast.success(
                        `Lauf abgeschlossen: ${d.applied_count} angewendet, ${d.skipped_low_score} übersprungen, ${d.failed_count} Fehler`
                      );
                      setHistoryRefresh((n) => n + 1);
                    } catch (e) {
                      toast.error(
                        e?.response?.data?.detail || "Manueller Lauf fehlgeschlagen."
                      );
                    } finally {
                      setAutoMatchRunning(false);
                    }
                  }}
                  disabled={autoMatchRunning}
                  className="h-7 text-[11px]"
                  data-testid="settings-auto-bank-match-run-now"
                >
                  {autoMatchRunning ? (
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  ) : (
                    <Play className="h-3 w-3 mr-1.5" />
                  )}
                  Jetzt ausführen
                </Button>
              </div>
              <AutoMatchHistory key={historyRefresh} />
            </div>
            <SectionSave section="bankmatch" />
          </div>

          {/* KI-gestütztes QR-Matching (Iter 109c/d) */}
          <div id="section-aimatch" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">KI</span>
              KI-Match für eingehende Bank-Zahlungen
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              Wenn QR-Referenz und exakter Betrag keinen Treffer liefern, prüft Claude Sonnet 4.5 die
              Top-Kandidaten anhand von Debitor-Name, Verwendungszweck und Datum. Nur Matches über dem
              Schwellwert werden automatisch verbucht. Funktioniert für camt.054 (PostFinance MFTPF)
              und camt.053 (alle Banken).
            </p>
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 mb-3">
              <input
                type="checkbox"
                id="ai-match-enabled"
                checked={!!form.ai_match_enabled}
                onChange={(e) => setForm({ ...form, ai_match_enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300"
                data-testid="settings-ai-match-toggle"
              />
              <Label
                htmlFor="ai-match-enabled"
                className="cursor-pointer text-sm font-medium text-slate-900"
              >
                KI-Match-Fallback aktivieren
              </Label>
              <span
                className={`ml-auto inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  form.ai_match_enabled
                    ? "bg-violet-100 text-violet-800"
                    : "bg-slate-200 text-slate-600"
                }`}
              >
                {form.ai_match_enabled ? "AN" : "AUS"}
              </span>
            </div>
            {form.ai_match_enabled && (
              <div className="space-y-2">
                <Label htmlFor="ai-match-threshold" className="flex items-center justify-between">
                  <span>Mindest-Confidence (Vorsicht ↔ Tempo)</span>
                  <span className="font-mono text-violet-700 text-sm" data-testid="settings-ai-match-threshold-value">
                    {Math.round((Number(form.ai_match_confidence_threshold) || 0.85) * 100)}%
                  </span>
                </Label>
                <input
                  id="ai-match-threshold"
                  type="range"
                  min="0.50"
                  max="0.99"
                  step="0.01"
                  value={Number(form.ai_match_confidence_threshold) || 0.85}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      ai_match_confidence_threshold: Number(e.target.value),
                    })
                  }
                  className="w-full accent-violet-600"
                  data-testid="settings-ai-match-threshold-slider"
                />
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>50% · sehr offensiv</span>
                  <span className="text-slate-600">Empfohlen: 85%</span>
                  <span>99% · paranoid vorsichtig</span>
                </div>
                <p className="text-[11px] text-slate-500 pt-1">
                  <strong>Empfehlung:</strong> Default <strong>85%</strong> für die meisten Firmen. <strong>75%</strong> für
                  Power-User mit sauberen Stammdaten und schnellen Reviews. <strong>≥ 90%</strong> für Firmen mit
                  Compliance-Anforderungen (z.B. Wirtschaftsprüfung).
                </p>
              </div>
            )}
            <SectionSave section="aimatch" />
          </div>

          {/* Vendor-Rules (Auto-Lern-Konto) */}
          <div id="section-vendor" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2">
              Auto-Lern-Konto (Vendor-Regeln)
            </h2>
            <VendorRulesManager companyId={companyId} />
          </div>

          {/* Karten-Register: Firmenkarten / Privatkarten (Spesen) */}
          <div id="section-cards" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-cards-section">
            <h2 className="text-sm font-medium text-slate-800 mb-2">Firmenkarten & Privatkarten</h2>
            <CardsManager companyId={companyId} />
          </div>

          {/* Iter313: Kontierungsregeln + Spesen */}
          <div id="section-split-rules" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-split-rules-section">
            <SplitRulesCard companyId={company?._id} />
          </div>
          <div id="section-booking-rules" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-booking-rules-section">
            <h2 className="text-sm font-medium text-slate-800 mb-2">Kontierungsregeln & Spesen-Rückerstattung</h2>
            <BookingRulesManager companyId={companyId} />
          </div>

          {/* Schritt 3: Konfidenz-Routing / Auto-Buchung */}
          <div id="section-inbox-routing" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-inbox-routing-section">
            <h2 className="text-sm font-medium text-slate-800 mb-1">Auto-Buchung (Konfidenz-Routing)</h2>
            <p className="text-[11px] text-slate-500 mb-2">Standard ist manuell: jeder Beleg landet in der Freigabe-Queue. Das System rechnet im Schatten mit, wie viele Belege automatisch verbucht worden wären – schalte die Automatik erst ein, wenn die Empfehlung grün ist.</p>
            <InboxRoutingPanel companyId={companyId} mode="settings" />
          </div>

          {/* PDF-Branding */}
          <div id="section-branding" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2">Rechnungs-Design (PDF)</h2>
            <p className="text-xs text-slate-500 mb-3">
              Eigenes Logo, Akzentfarben und Fußzeile für Ihre Rechnungs-PDFs. Diese Angaben werden
              automatisch beim Generieren der PDF verwendet.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Firmenlogo (PNG/JPEG, max. 500 KB)</Label>
                <div className="flex items-start gap-3">
                  <div
                    className="h-20 w-40 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0"
                    data-testid="settings-logo-preview"
                  >
                    {form.logo_data_url ? (
                      <img src={form.logo_data_url} alt="Vorschau" className="max-h-20 max-w-40 object-contain" />
                    ) : (
                      <span className="text-[10px] text-slate-400">Keine Vorschau</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      data-testid="settings-logo-upload-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const { dataUrl, width, height, resized } = await resizeImageFile(f);
                          setForm({ ...form, logo_data_url: dataUrl });
                          toast.success(resized
                            ? `Logo automatisch auf ${width}×${height} px verkleinert (Seitenverhältnis unverändert)`
                            : "Logo übernommen");
                        } catch {
                          toast.error("Logo konnte nicht gelesen werden");
                        }
                      }}
                    />
                    {form.logo_data_url && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-fit"
                        onClick={() => setForm({ ...form, logo_data_url: "" })}
                        data-testid="settings-logo-remove-button"
                      >
                        Logo entfernen
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="primary-color">Primärfarbe</Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="primary-color"
                      type="color"
                      className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                      value={form.primary_color || "#0f172a"}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      data-testid="settings-primary-color-input"
                    />
                    <Input
                      value={form.primary_color || ""}
                      onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                      placeholder="#0f172a"
                      className="flex-1 font-mono text-xs"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="accent-color">Akzentfarbe</Label>
                  <div className="flex items-center gap-2">
                    <input
                      id="accent-color"
                      type="color"
                      className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                      value={form.accent_color || "#10b981"}
                      onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                      data-testid="settings-accent-color-input"
                    />
                    <Input
                      value={form.accent_color || ""}
                      onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                      placeholder="#10b981"
                      className="flex-1 font-mono text-xs"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-1">
              <Label htmlFor="invoice-footer">Fußzeile (Mehrzeilig)</Label>
              <textarea
                id="invoice-footer"
                rows={3}
                value={form.invoice_footer}
                onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })}
                placeholder="z.B. Vielen Dank · IBAN CH00 0000 0000 · zahlbar innert 30 Tagen · info@firma.ch"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="settings-invoice-footer-input"
              />
            </div>

            {/* Iter66.5: Branding-Preview Buttons */}
            <div className="mt-4 pt-4 border-t border-dashed border-slate-200" data-testid="settings-branding-preview-block">
              <p className="text-xs text-slate-600 mb-2">
                💡 <b>Tipp:</b> Schau Dir vor dem Speichern an, wie das Branding auf einer PDF wirkt.
                Speichere zuerst die Änderungen, dann klicke auf eine Vorschau:
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const cid = localStorage.getItem("demoCompanyId");
                    if (!cid) return;
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/companies/${cid}/branding/preview-pdf?type=invoice`;
                    authFetch(url)
                      .then((r) => r.blob())
                      .then((blob) => window.open(URL.createObjectURL(blob), "_blank"))
                      .catch(() => toast.error("Vorschau fehlgeschlagen — bitte zuerst speichern."));
                  }}
                  data-testid="settings-branding-preview-invoice-btn"
                >
                  📄 Vorschau Rechnung
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const cid = localStorage.getItem("demoCompanyId");
                    if (!cid) return;
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/companies/${cid}/branding/preview-pdf?type=dunning`;
                    authFetch(url)
                      .then((r) => r.blob())
                      .then((blob) => window.open(URL.createObjectURL(blob), "_blank"))
                      .catch(() => toast.error("Vorschau fehlgeschlagen — bitte zuerst speichern."));
                  }}
                  data-testid="settings-branding-preview-dunning-btn"
                >
                  📬 Vorschau Mahnung
                </Button>
              </div>
            </div>
            <SectionSave section="branding" />
          </div>

          {/* Anhang OR Art. 959c */}
          <div
            id="section-anhang"
            className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20"
            data-testid="settings-anhang-section"
          >
            <div>
              <h2 className="text-sm font-medium text-slate-800 mb-1">Anhang zur Jahresrechnung (OR Art. 959c)</h2>
              <p className="text-xs text-slate-500">
                Pflichtangaben für die Jahresrechnung — werden im Anhang-PDF und in der Reports-Ansicht ausgewiesen.
              </p>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="fte-average">
                  Ø Vollzeitstellen (FTE) im Geschäftsjahr
                </Label>
                <Input
                  id="fte-average"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="z.B. 4.5"
                  value={form.fte_average}
                  onChange={(e) => setForm({ ...form, fte_average: e.target.value })}
                  data-testid="settings-fte-average-input"
                  disabled={isViewer}
                />
                <p className="text-[11px] text-slate-500">
                  OR Art. 959c Abs. 2 Ziff. 10 — Auf eine Dezimalstelle genau, Jahresdurchschnitt.
                </p>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Label htmlFor="notes-principles">
                Bewertungsgrundsätze (1 Punkt pro Zeile)
              </Label>
              <textarea
                id="notes-principles"
                rows={6}
                value={form.notes_principles}
                onChange={(e) => setForm({ ...form, notes_principles: e.target.value })}
                placeholder={
                  "Die Jahresrechnung wurde nach OR (Art. 957–963b) erstellt.\n" +
                  "Aktiven werden zu Anschaffungskosten abzüglich notwendiger Wertberichtigungen bewertet.\n" +
                  "Sachanlagen werden linear über die geschätzte Nutzungsdauer abgeschrieben.\n" +
                  "Forderungen werden zum Nominalwert abzüglich notwendiger Wertberichtigungen bilanziert."
                }
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="settings-notes-principles-input"
                disabled={isViewer}
              />
              <p className="text-[11px] text-slate-500">
                Leer lassen → Standard-Grundsätze werden verwendet.
              </p>
            </div>
            <SectionSave section="anhang" />
          </div>

          {/* Mahnwesen (Dunning) */}
          <div id="section-facturx" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-facturx-section">
            <FacturXSettingsCard companyId={company?._id} />
          </div>
          <div id="section-dpa" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-dpa-section">
            <DpaCard companyId={company?._id} />
          </div>
          <div id="section-crm" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20" data-testid="settings-crm-section">
            <CrmConnectorCard companyId={company?._id} />
          </div>
          <div id="section-mahnwesen" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-medium text-slate-800 mb-1">Mahnwesen</h2>
                <p className="text-xs text-slate-500">
                  3-stufiges Mahnsystem mit PDF-Versand und Swiss QR-Zahlteil (inkl. Mahngebühr).
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="settings-dunning-enabled-label">
                <input
                  type="checkbox"
                  checked={form.dunning_enabled}
                  onChange={(e) => setForm({ ...form, dunning_enabled: e.target.checked })}
                  data-testid="settings-dunning-enabled-input"
                />
                <span className="text-slate-700">Aktiv</span>
              </label>
            </div>

            <label className="inline-flex items-center gap-2 mt-3 text-xs cursor-pointer" data-testid="settings-dunning-addfee-label">
              <input
                type="checkbox"
                checked={form.dunning_add_fee_to_total}
                onChange={(e) => setForm({ ...form, dunning_add_fee_to_total: e.target.checked })}
                data-testid="settings-dunning-addfee-input"
              />
              <span className="text-slate-700">
                Mahngebühr in die neue Rechnungssumme einrechnen (QR-Betrag enthält Gebühr)
              </span>
            </label>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((lvl) => (
                <div
                  key={lvl}
                  className="border border-slate-200 rounded-lg p-3 bg-slate-50/50"
                  data-testid={`settings-dunning-level-${lvl}`}
                >
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    Stufe {lvl} — {lvl === 1 ? "Zahlungserinnerung" : lvl === 2 ? "1. Mahnung" : "Letzte Mahnung"}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-[11px]">Tage nach Fälligkeit</Label>
                      <Input
                        type="number"
                        min={1}
                        value={form[`dunning_level_${lvl}_days`]}
                        onChange={(e) => setForm({ ...form, [`dunning_level_${lvl}_days`]: parseInt(e.target.value, 10) || 0 })}
                        data-testid={`settings-dunning-level-${lvl}-days`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Mahngebühr (CHF)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.05}
                        value={form[`dunning_level_${lvl}_fee`]}
                        onChange={(e) => setForm({ ...form, [`dunning_level_${lvl}_fee`]: parseFloat(e.target.value) || 0 })}
                        data-testid={`settings-dunning-level-${lvl}-fee`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 max-w-xs">
              <Label className="text-[11px]">Verzugszins (% pro Jahr, ab Mahnstufe 2)</Label>
              <Input
                type="number"
                min={0}
                max={15}
                step={0.1}
                value={form.dunning_interest_rate_percent}
                onChange={(e) => setForm({ ...form, dunning_interest_rate_percent: parseFloat(e.target.value) || 0 })}
                data-testid="settings-dunning-interest-rate"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                0 = kein Verzugszins. Gesetzlich zulässig in CH: 5% (OR Art. 104). Basis: 360 Tage.
              </p>
            </div>
            <SectionSave section="mahnwesen" />
          </div>

          {/* E-Mail / SMTP */}
          <div id="section-smtp" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2">E-Mail / SMTP (z.B. Gmail)</h2>
            <p className="text-xs text-slate-500 mb-3">
              Hier können Sie die SMTP-Daten Ihrer Firma hinterlegen, z.B. für Passwort-Reset-Mails.
              Für Gmail benötigen Sie ein <strong>App-Passwort</strong> und müssen &quot;Weniger sichere Apps&quot; bzw.
              den entsprechenden Zugriff erlauben.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="smtp-host">SMTP-Host</Label>
                <Input
                  id="smtp-host"
                  value={form.smtp_host}
                  onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
                  placeholder="z.B. smtp.gmail.com"
                  data-testid="settings-smtp-host-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="smtp-port">Port</Label>
                <Input
                  id="smtp-port"
                  type="number"
                  value={form.smtp_port}
                  onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
                  placeholder="z.B. 587"
                  data-testid="settings-smtp-port-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="smtp-use-tls">TLS verwenden</Label>
                <Select
                  value={form.smtp_use_tls ? "true" : "false"}
                  onValueChange={(value) => setForm({ ...form, smtp_use_tls: value === "true" })}
                >
                  <SelectTrigger id="smtp-use-tls" data-testid="settings-smtp-tls-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Ja (empfohlen)</SelectItem>
                    <SelectItem value="false">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <div className="space-y-1">
                <Label htmlFor="smtp-username">Benutzername</Label>
                <Input
                  id="smtp-username"
                  value={form.smtp_username}
                  onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
                  placeholder="Gmail-Adresse"
                  data-testid="settings-smtp-username-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="smtp-password">Passwort / App-Passwort</Label>
                <Input
                  id="smtp-password"
                  type="password"
                  value={form.smtp_password}
                  onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
                  data-testid="settings-smtp-password-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="smtp-from-email">Absender-E-Mail</Label>
                <Input
                  id="smtp-from-email"
                  type="email"
                  value={form.smtp_from_email}
                  onChange={(e) => setForm({ ...form, smtp_from_email: e.target.value })}
                  placeholder="z.B. noreply@ihrefirma.ch"
                  data-testid="settings-smtp-from-email-input"
                />
              </div>
            </div>

            {/* Test-E-Mail versenden */}
            <div className="mt-4 flex flex-col md:flex-row gap-2 md:items-end">
              <div className="flex-1 space-y-1">
                <Label htmlFor="smtp-test-to">Test-E-Mail an</Label>
                <Input
                  id="smtp-test-to"
                  type="email"
                  value={smtpTestTo}
                  onChange={(e) => setSmtpTestTo(e.target.value)}
                  placeholder="deine@email.ch"
                  data-testid="settings-smtp-test-to-input"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={smtpTesting || !smtpTestTo}
                onClick={handleSmtpTest}
                data-testid="settings-smtp-test-button"
              >
                {smtpTesting ? "Sende..." : "Test-E-Mail senden"}
              </Button>
            </div>
            {smtpTestResult && (
              <div
                className={
                  "mt-2 text-xs " +
                  (smtpTestResult.ok ? "text-emerald-700" : "text-rose-600")
                }
                data-testid="settings-smtp-test-result"
              >
                {smtpTestResult.message}
              </div>
            )}
            <SectionSave section="smtp" />
          </div>

          {/* OCR-Konfiguration */}
          <div id="section-ocr" className="pt-4 border-t border-dashed border-slate-200 mt-4 scroll-mt-20">
            <h2 className="text-sm font-medium text-slate-800 mb-2">Beleg-OCR (Vorbereitung)</h2>
            <p className="text-xs text-slate-500 mb-3">
              Hier können Sie einen externen OCR-Dienst vorbereiten (z.B. eigene HTTP-API) für die Beleganalyse.
              Aktuell wird nur die interne Textanalyse verwendet, spätere OCR-Aufrufe können diese Felder nutzen.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ocr-provider">Provider-Bezeichner</Label>
                <Input
                  id="ocr-provider"
                  value={form.ocr_provider}
                  onChange={(e) => setForm({ ...form, ocr_provider: e.target.value })}
                  placeholder="z.B. custom_rest"
                  data-testid="settings-ocr-provider-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ocr-api-url">OCR API-URL</Label>
                <Input
                  id="ocr-api-url"
                  value={form.ocr_api_url}
                  onChange={(e) => setForm({ ...form, ocr_api_url: e.target.value })}
                  placeholder="https://api.mein-ocr-dienst.ch/"
                  data-testid="settings-ocr-api-url-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ocr-api-key">API-Key</Label>
                <Input
                  id="ocr-api-key"
                  type="password"
                  value={form.ocr_api_key}
                  onChange={(e) => setForm({ ...form, ocr_api_key: e.target.value })}
                  data-testid="settings-ocr-api-key-input"
                />
              </div>
            </div>
            <SectionSave section="ocr" />
          </div>

          {/* Standardkonten */}
          <div id="section-konten" className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 scroll-mt-20">
            <div className="space-y-1">
              <Label htmlFor="debtor-account">Standard Debitor (Forderungen)</Label>
              <Select
                value={form.default_debtor_account_id}
                onValueChange={(value) => setForm({ ...form, default_debtor_account_id: value })}
              >
                <SelectTrigger id="debtor-account" data-testid="settings-debtor-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="revenue-account">Standard Erlös</Label>
              <Select
                value={form.default_revenue_account_id}
                onValueChange={(value) => setForm({ ...form, default_revenue_account_id: value })}
              >
                <SelectTrigger id="revenue-account" data-testid="settings-revenue-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="creditor-account">Standard Kreditor (Verbindlichkeiten)</Label>
              <Select
                value={form.default_creditor_account_id}
                onValueChange={(value) => setForm({ ...form, default_creditor_account_id: value })}
              >
                <SelectTrigger id="creditor-account" data-testid="settings-creditor-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div id="section-konten-accounts" className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="bank-account">Standard Bank</Label>
              <Select
                value={form.default_bank_account_id}
                onValueChange={(value) => setForm({ ...form, default_bank_account_id: value })}
              >
                <SelectTrigger id="bank-account" data-testid="settings-bank-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cash-account">Standard Kasse</Label>
              <Select
                value={form.default_cash_account_id}
                onValueChange={(value) => setForm({ ...form, default_cash_account_id: value })}
              >
                <SelectTrigger id="cash-account" data-testid="settings-cash-account-select">
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cc-account">
                Standard Kreditkarte
                <span className="ml-1 text-[10px] font-normal text-slate-400">(camt CCRD/POSD)</span>
              </Label>
              <Select
                value={form.default_credit_card_account_id || "__none__"}
                onValueChange={(value) =>
                  setForm({
                    ...form,
                    default_credit_card_account_id: value === "__none__" ? "" : value,
                  })
                }
              >
                <SelectTrigger id="cc-account" data-testid="settings-cc-account-select">
                  <SelectValue placeholder="(nicht gesetzt)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— (Karten-Käufe aufs Bankkonto) —</SelectItem>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 leading-snug">
                Wenn gesetzt, werden Karten-Käufe aus camt.053-Imports (BkTxCd PMNT/CCRD/POSD)
                automatisch hierauf gebucht statt aufs Bankkonto.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="salary-expense-account">Lohnaufwand</Label>
              <Select
                value={form.default_salary_expense_account_id}
                onValueChange={(value) =>
                  setForm({ ...form, default_salary_expense_account_id: value })
                }
              >
                <SelectTrigger
                  id="salary-expense-account"
                  data-testid="settings-salary-expense-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div id="section-payroll" className="space-y-1 scroll-mt-20">
              <div className="mb-3 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                <div className="text-xs text-emerald-900">
                  <b>Lohn-Konten-Assistent:</b> legt fehlende Standard-Konten an (5000, 5700, 5720, 2270, 2260, 2279) und setzt leere Felder automatisch.
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="bg-emerald-700 hover:bg-emerald-800 flex-shrink-0 ml-3"
                  title="Fehlende Lohn-Konten anlegen und Einstellungen automatisch setzen"
                  data-testid="payroll-auto-setup-button"
                  onClick={async () => {
                    try {
                      const r = await apiClient.post(`/companies/${company._id}/payroll-accounts/auto-setup`);
                      const created = r.data.created_accounts || [];
                      const set = r.data.settings_set || {};
                      const accRes = await apiClient.get(`/companies/${company._id}/accounts`);
                      setAccounts(accRes.data || []);
                      setForm((prev) => ({ ...prev, ...set }));
                      toast.success(
                        created.length || Object.keys(set).length
                          ? `Eingerichtet: ${created.length} Konto/Konten angelegt, ${Object.keys(set).length} Einstellung(en) gesetzt`
                          : "Alles bereits eingerichtet — keine Änderungen nötig",
                      );
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Einrichtung fehlgeschlagen");
                    }
                  }}
                >
                  Automatisch einrichten
                </Button>
              </div>
              <Label htmlFor="payroll-social-emp-account">Lohn AN Sozialversicherungen</Label>
              <Select
                value={form.payroll_social_employee_account_id}
                onValueChange={(value) =>
                  setForm({ ...form, payroll_social_employee_account_id: value })
                }
              >
                <SelectTrigger
                  id="payroll-social-emp-account"
                  data-testid="settings-payroll-social-employee-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="payroll-social-er-account">Lohn AG Sozialversicherungen</Label>
              <Select
                value={form.payroll_social_employer_account_id}
                onValueChange={(value) =>
                  setForm({ ...form, payroll_social_employer_account_id: value })
                }
              >
                <SelectTrigger
                  id="payroll-social-er-account"
                  data-testid="settings-payroll-social-employer-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="payroll-pension-emp-account">Lohn AN BVG</Label>
              <Select
                value={form.payroll_pension_employee_account_id}
                onValueChange={(value) =>
                  setForm({ ...form, payroll_pension_employee_account_id: value })
                }
              >
                <SelectTrigger
                  id="payroll-pension-emp-account"
                  data-testid="settings-payroll-pension-employee-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="payroll-pension-er-account">Lohn AG BVG</Label>
              <Select
                value={form.payroll_pension_employer_account_id}
                onValueChange={(value) =>
                  setForm({ ...form, payroll_pension_employer_account_id: value })
                }
              >
                <SelectTrigger
                  id="payroll-pension-er-account"
                  data-testid="settings-payroll-pension-employer-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="payroll-tax-account">Lohn Quellensteuer</Label>
              <Select
                value={form.payroll_tax_withholding_account_id}
                onValueChange={(value) =>
                  setForm({ ...form, payroll_tax_withholding_account_id: value })
                }
              >
                <SelectTrigger
                  id="payroll-tax-account"
                  data-testid="settings-payroll-tax-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="vat-settlement-account">MWST-Abschlusskonto (ESTV)</Label>
              <Select
                value={form.default_vat_settlement_account_id}
                onValueChange={(value) => setForm({ ...form, default_vat_settlement_account_id: value })}
              >
                <SelectTrigger
                  id="vat-settlement-account"
                  data-testid="settings-vat-settlement-account-select"
                >
                  <SelectValue placeholder="Konto wählen" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc._id} value={acc.code}>
                      {acc.code} · {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div id="section-konten-save">
            <SectionSave section="konten" label="Konten speichern" />
          </div>
        </form>
      </Card>

      <SettingsSharesSection
        companyId={company?._id}
        isOwner={company?.current_user_role === "owner"}
      />

      <SettingsPostFinanceSection
        companyId={company?._id}
        isOwner={company?.current_user_role === "owner"}
      />

      <SettingsStrongfitSection
        companyId={company?._id}
        isOwner={company?.current_user_role === "owner"}
      />

      <SettingsWhatsAppSection isSuperAdmin={isSuperAdmin} />
      <div id="section-whatsapp-phones" className="pl-14 scroll-mt-20">
        <CompanyUploaderPhonesSection canWrite={company?.current_user_role === "owner" || company?.current_user_role === "advisor"} />
      </div>
      <SettingsReceiptRobotSection companyId={company?._id} />
      <SettingsEmailTrimRulesSection companyId={company?._id} />

      <SettingsTwoFactorSection />

      <SettingsCostCentersSection companyId={company?._id} />

      <SettingsDeveloperSection companyId={company?._id} />

      <HandbookCard />

      {/* MWST-Methode + Rhythmus */}
      <Card className="p-5 border border-slate-200 bg-white" id="section-vat" data-testid="settings-vat-section">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold">%</span>
          </div>
          <div>
            <h2 className="text-sm font-medium text-slate-900">MWST-Methode &amp; Rhythmus</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              ESTV-konforme MWST-Konfiguration. Effektive Methode (mit Vorsteuer) oder Saldosteuersatz (vereinfacht, ohne Vorsteuer).
              Seit 2025: jährliche Abrechnung möglich für Umsatz bis CHF 5 Mio.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">MWST-Methode</label>
            <select value={form.vat_method} onChange={(e) => setForm((f) => ({ ...f, vat_method: e.target.value }))}
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="vat-method-select">
              <option value="effective">Effektiv (mit Vorsteuer)</option>
              <option value="saldo">Saldosteuersatz</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">Saldosteuersatz % (nur bei Saldo)</label>
            <input type="number" step="0.1" value={form.vat_saldo_rate}
              onChange={(e) => setForm((f) => ({ ...f, vat_saldo_rate: e.target.value }))}
              placeholder="z.B. 5.1"
              disabled={form.vat_method !== "saldo"}
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5 disabled:bg-slate-100"
              data-testid="vat-saldo-rate-input" />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">Kürzungsschlüssel gemischte Verwendung % (Ziffer 415)</label>
            <input type="number" step="0.1" min="0" max="100" value={form.vat_mixed_use_key}
              onChange={(e) => setForm((f) => ({ ...f, vat_mixed_use_key: e.target.value }))}
              placeholder="z.B. 20 (leer = keine Korrektur)"
              disabled={form.vat_method === "saldo"}
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5 disabled:bg-slate-100"
              data-testid="vat-mixed-use-key-input" />
            <p className="text-[10px] text-slate-500">Anteil nicht-steuerbarer Verwendung. Ziffer 415 = Vorsteuer (400+405+410) × Schlüssel – automatisch berechnet, Buchung per Klick im MWST-Tab.</p>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">Abrechnungsrhythmus</label>
            <select value={form.vat_reporting_period} onChange={(e) => setForm((f) => ({ ...f, vat_reporting_period: e.target.value }))}
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="vat-period-select-settings">
              <option value="monthly">Monatlich (auf Antrag, z. B. bei Vorsteuerüberschüssen)</option>
              <option value="quarterly">Quartalsweise (Standard)</option>
              <option value="semiannual">Halbjährlich (Saldo)</option>
              <option value="annual">Jährlich mit Raten (seit 2025, &lt; 5 Mio Umsatz)</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div className="space-y-1 md:col-span-2">
            <label className="text-[11px] text-slate-700">Abrechnungsart (ESTV-Bewilligung)</label>
            <select value={form.vat_accounting_basis} onChange={(e) => setForm((f) => ({ ...f, vat_accounting_basis: e.target.value }))}
              disabled={vatClosures.length > 0}
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5 disabled:bg-slate-100 disabled:text-slate-500" data-testid="vat-accounting-basis-select">
              <option value="agreed">Vereinbartes Entgelt – MWST bei Rechnungsstellung (Standard)</option>
              <option value="received">Vereinnahmtes Entgelt – MWST bei Zahlungseingang/-ausgang</option>
            </select>
            {vatClosures.length > 0 && (
              <p className="text-[11px] text-amber-700 font-medium" data-testid="vat-accounting-basis-locked-hint">
                Gesperrt – {vatClosures.map((c) => `${c.period} ${c.year}`).join(", ")} {vatClosures.length === 1 ? "ist" : "sind"} definitiv abgerechnet.
                Ein Wechsel der Abrechnungsart ist nur per ESTV-Bewilligung auf einen Periodenbeginn möglich und würde die archivierten Abrechnungen widersprüchlich machen.
              </p>
            )}
            <p className="text-[10px] text-slate-500">
              {form.vat_accounting_basis === "received"
                ? "Die Abrechnung zählt Umsatz- und Vorsteuer anteilig nach Zahlungsdatum. Die Buchhaltung bucht die MWST weiterhin bei Rechnungsstellung – die Differenz zum Konto 2200/1170 entspricht der noch nicht fälligen MWST auf offenen Debitoren/Kreditoren."
                : "Umsatz- und Vorsteuer werden mit dem Rechnungsdatum deklariert, unabhängig vom Zahlungseingang."}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-slate-100">
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">MWST-pflichtig?</label>
            <select value={form.vat_registered ? "yes" : "no"}
              onChange={(e) => setForm((f) => ({ ...f, vat_registered: e.target.value === "yes" }))}
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="vat-registered-select">
              <option value="no">Nein (nicht im MWST-Register)</option>
              <option value="yes">Ja (MWST-pflichtig)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">MWST-UID</label>
            <input type="text" value={form.vat_uid}
              onChange={(e) => setForm((f) => ({ ...f, vat_uid: e.target.value }))}
              placeholder="CHE-123.456.789 MWST"
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5"
              data-testid="vat-uid-input" />
          </div>
          <div className="space-y-1">
            <label className="text-[11px] text-slate-700">Vorsteuerkonto (effektive Methode)</label>
            <input type="text" value={form.default_vat_input_account_id}
              onChange={(e) => setForm((f) => ({ ...f, default_vat_input_account_id: e.target.value }))}
              placeholder="1170"
              className="w-full text-sm rounded-md border border-slate-200 px-2 py-1.5"
              data-testid="vat-input-account-input" />
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300"
              checked={!!form.autobook_enabled}
              onChange={(e) => setForm((f) => ({ ...f, autobook_enabled: e.target.checked }))}
              data-testid="autobook-enabled-checkbox" />
            <span className="text-xs text-slate-700">
              <span className="font-medium">Automatisch verbuchen (KI)</span><br />
              <span className="text-slate-500">
                Aus: Die KI schlägt das Konto nur vor — du oder der Treuhänder bestätigt.
                Ein: Belege werden automatisch verbucht, sobald derselbe Lieferant mindestens
                5× auf dasselbe Konto gebucht wurde (mit Doppel-Kontrolle).
              </span>
            </span>
          </label>
        </div>
        <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          💡 Die Berechnung der MWST-Abrechnung findest du im Bereich <a href="/reports" className="underline font-semibold">Auswertungen → MWST</a>.
        </div>
        <SectionSave section="vat" />
        {form.vat_reporting_period === "annual" && (
          <div className="mt-3 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="vat-annual-installments-hint">
            Jährliche Abrechnung: Die ESTV verlangt Raten ({form.vat_method === "effective" ? "3 × 25 %" : "1 × 50 %"} der Vorjahressteuer). Fälligkeiten und Beträge siehst du unter <a href="/reports?tab=vat" className="underline font-semibold">Auswertungen → MWST → Raten</a> und auf dem Dashboard.
          </div>
        )}
        <SettingsVatRatesSection companyId={company?._id} isViewer={isViewer} />
        <ReverseChargeSuppliersCard companyId={company?._id} isViewer={isViewer} />
      </Card>

      <SettingsGlobalAllowlistSection isSuperAdmin={isSuperAdmin} />

      <SettingsAccountsSection
        companyId={company?._id}
        isViewer={isViewer}
        onAccountsChange={handleAccountsChange}
      />

      <div id="section-ai-learning" className="scroll-mt-20">
        <SettingsAiLearningSection companyId={company?._id} />
      </div>
      <div id="section-ai-usage" className="scroll-mt-20">
        <AiUsageSettingsCard companyId={company?._id} />
      </div>
        </div>
      </div>
    </div>
  );
}
